/**
 * @file benefitPlanService.js
 * @description Service layer for handling operations related to Benefit Plans.
 */
import { clientApiHttp } from './httpService';
import { getNameForDuplicate } from '../utils/utils';

/**
 * Retrieve the tenant ID from local storage.
 * @returns {number} The tenant ID
 */
const getTenantId = () => +window.localStorage.getItem('tenantId');

/**
 * Searches for benefit plans with pagination and filtering options.
 *
 * @param {number} tenantId - The ID of the tenant
 * @param {number} page - The page number for pagination
 * @param {number} pageSize - The number of items per page
 * @param {string} search - Search term for filtering benefit plans
 * @param {string} sort - Sort field (e.g., "name", "code", "planType", "policy", "networkType", "vendor", "isActive")
 * @param {string} order - Sort order ("asc" or "desc")
 * @returns {Promise<Object>} A promise that resolves to the paginated benefit plan list
 */
export const searchBenefitPlans = async (
    tenantId = null,
    page = 1,
    pageSize = 20,
    search = '',
    sort = 'default',
    order = 'default',
) => {
    try {
        if (!tenantId) tenantId = getTenantId();
        const params = new URLSearchParams({
            page,
            pageSize,
            sort,
            order,
        });

        if (search) params.append('search', search);

        const response = await clientApiHttp.get(
            `/v2/client/${tenantId}/plan?${params.toString()}`,
        );
        return response.data;
    } catch (error) {
        console.error('Error searching benefit plans:', error);
        throw error;
    }
};

/**
 * Fetches a benefit plan by ID.
 *
 * @param {number} planId - The ID of the benefit plan
 * @returns {Promise<Object>} A promise that resolves to the benefit plan data
 */
export const fetchBenefitPlanById = async planId => {
    try {
        const tenantId = getTenantId();
        const response = await clientApiHttp.get(
            `/v2/client/${tenantId}/plan/${planId}`,
        );
        return response.data;
    } catch (error) {
        console.error(`Error fetching benefit plan with ID ${planId}:`, error);
        throw error;
    }
};

/**
 * Creates a new benefit plan.
 *
 * @param {Object} planData - The data for the new benefit plan
 * @returns {Promise<Object>} A promise that resolves to the created benefit plan data
 */
export const createBenefitPlan = async planData => {
    try {
        const tenantId = getTenantId();
        const formattedData = formatPlanDataForApi(planData, tenantId);

        const response = await clientApiHttp.post(
            `/v2/client/${tenantId}/plan`,
            formattedData,
        );
        return response.data;
    } catch (error) {
        console.error('Error creating benefit plan:', error);
        throw error;
    }
};

/**
 * Updates an existing benefit plan.
 *
 * @param {Object} planData - The updated data for the benefit plan
 * @returns {Promise<Object>} A promise that resolves to the updated benefit plan data
 */
export const updateBenefitPlan = async planData => {
    try {
        const tenantId = getTenantId();
        const formattedData = formatPlanDataForApi(planData, tenantId);

        const response = await clientApiHttp.put(
            `/v2/client/${tenantId}/plan`,
            formattedData,
        );
        return response.data;
    } catch (error) {
        console.error('Error updating benefit plan:', error);
        throw error;
    }
};

/**
 * Deletes a benefit plan by ID.
 *
 * @param {number} planId - The ID of the benefit plan to delete
 * @returns {Promise<Object>} A promise that resolves to the deletion status
 */
export const deleteBenefitPlan = async planId => {
    try {
        const tenantId = getTenantId();
        const response = await clientApiHttp.delete(
            `/v2/client/${tenantId}/plan/${planId}`,
        );
        return response.data;
    } catch (error) {
        console.error(`Error deleting benefit plan with ID ${planId}:`, error);
        throw error;
    }
};

/**
 * Formats plan details to match the API requirements by nulling out the "plan" property.
 *
 * @param {Array} planDetails - The plan details from the UI
 * @returns {Array} Formatted plan details for the API
 */
const formatPlanDetails = planDetails => {
    return planDetails.map(detail => ({
        ...detail,
        plan: null,
    }));
};

/**
 * Formats plan data to match the API requirements.
 *
 * @param {Object} planData - The plan data from the UI
 * @param {number} tenantId - The tenant ID
 * @returns {Object} Formatted plan data for the API
 */
const formatPlanDataForApi = (planData, tenantId) => {
    return {
        id: planData.id || 0,
        tenantId: tenantId,
        planTypeId: planData.planTypeId,
        vendorId: planData.vendorId || 0,
        name: planData.name,
        code: planData.code,
        networkTypeId: planData.networkTypeId || null,
        networkName: planData.networkName || null,
        policy: planData.policy || null,
        phoneNumber: planData.phoneNumber || null,
        url: planData.url || null,
        tenantSSOId: planData.tenantSSOId || null,
        importId: planData.importId || null,
        overrideName: planData.overrideName || null,
        providerList: planData.providerList || null,
        isActive: planData.isActive !== undefined ? planData.isActive : true,
        isVisible: planData.isVisible !== undefined ? planData.isVisible : true,
        headerDisplay: planData.headerDisplay || null,
        planAssets: formatPlanAssets(planData.planAssets || []),
        planDetails: formatPlanDetails(planData.planDetails || []),
        planDetailCategories: planData.planDetailCategories || [],
    };
};

/**
 * Formats plan assets to match the API requirements.
 *
 * @param {Array} planAssets - The plan assets from the UI
 * @returns {Array} Formatted plan assets for the API
 */
const formatPlanAssets = planAssets => {
    return planAssets.map(asset => ({
        id: asset.id || 0,
        planId: asset.planId || 0,
        assetId: asset.assetId,
    }));
};

/**
 * Attaches an asset to a benefit plan.
 *
 * @param {number} planId - The ID of the benefit plan
 * @param {number} assetId - The ID of the asset to attach
 * @returns {Promise<Object>} A promise that resolves to the updated benefit plan data
 */
export const attachAssetToBenefitPlan = async (planId, assetId) => {
    try {
        const plan = await fetchBenefitPlanById(planId);

        if (!plan.planAssets) {
            plan.planAssets = [];
        }

        const assetAlreadyAttached = plan.planAssets.some(
            asset => asset.assetId === assetId,
        );

        if (!assetAlreadyAttached) {
            plan.planAssets.push({
                id: 0,
                planId: planId,
                assetId: assetId,
            });
        }

        return await updateBenefitPlan(plan);
    } catch (error) {
        console.error(
            `Error attaching asset ${assetId} to benefit plan ${planId}:`,
            error,
        );
        throw error;
    }
};

/**
 * Removes an asset from a benefit plan.
 *
 * @param {number} planId - The ID of the benefit plan
 * @param {number} assetId - The ID of the asset to remove
 * @returns {Promise<Object>} A promise that resolves to the updated benefit plan data
 */
export const removeAssetFromBenefitPlan = async (planId, assetId) => {
    try {
        const plan = await fetchBenefitPlanById(planId);

        if (plan.planAssets) {
            plan.planAssets = plan.planAssets.filter(
                asset => asset.assetId !== assetId,
            );
        }

        return await updateBenefitPlan(plan);
    } catch (error) {
        console.error(
            `Error removing asset ${assetId} from benefit plan ${planId}:`,
            error,
        );
        throw error;
    }
};

/**
 * Fetches all benefit plans for a tenant.
 *
 * @returns {Promise<Array>} A promise that resolves to an array of benefit plans.
 */
export const fetchAllBenefitPlans = async () => {
    try {
        const tenantId = getTenantId();
        const response = await clientApiHttp.get(`/v2/client/${tenantId}/plan`);
        return response.data;
    } catch (error) {
        console.error('Error fetching all benefit plans:', error);
        throw error;
    }
};

/**
 * Searches for plan types for a tenant.
 * Supports both old paginated response shape ({ viewModels }) and new flat array.
 *
 * @param {number} tenantId - The ID of the tenant
 * @param {number} planYearId - Plan year ID (required once new backend is deployed)
 * @returns {Promise<Array>} A promise that resolves to the plan type list
 */
export const searchPlanTypes = async (tenantId = null, planYearId = null) => {
    try {
        if (!tenantId) tenantId = getTenantId();
        const params = new URLSearchParams();
        if (planYearId) params.append('planYearId', planYearId);

        const response = await clientApiHttp.get(
            `/v2/client/${tenantId}/plan-type?${params.toString()}`,
        );
        const data = response.data;
        // Handle both old paginated shape and new flat array
        return Array.isArray(data) ? data : (data?.viewModels ?? []);
    } catch (error) {
        console.error('Error searching plan types:', error);
        throw error;
    }
};

/**
 * Fetches all plan types for a tenant.
 *
 * @param {number} planYearId - Required plan year ID
 * @returns {Promise<Array>} A promise that resolves to an array of plan types.
 */
export const fetchAllPlanTypes = async (planYearId = null) => {
    try {
        const tenantId = getTenantId();
        const params = new URLSearchParams();
        if (planYearId) params.append('planYearId', planYearId);

        const response = await clientApiHttp.get(
            `/v2/client/${tenantId}/plan-type?${params.toString()}`,
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching all plan types:', error);
        throw error;
    }
};

/**
 * Updates an existing ClientPlanType.
 *
 * @param {Object} planTypeData - The updated ClientPlanType (must include `id`)
 * @returns {Promise<Object>} A promise that resolves to the updated record
 */
export const updatePlanType = async planTypeData => {
    try {
        if (!planTypeData?.id) {
            throw new TypeError('updatePlanType requires planTypeData.id');
        }
        const tenantId = getTenantId();
        const payload = {
            id: planTypeData.id,
            planTypeId: planTypeData.planTypeId ?? null,
            duplicatedFromPlanTypeId:
                planTypeData.duplicatedFromPlanTypeId ?? null,
            name: planTypeData.name ?? planTypeData.nameOverride ?? null,
            code: planTypeData.code ?? null,
            planYearId: planTypeData.planYearId,
            populationGroupIds: planTypeData.populationGroupIds ?? [],
            planTypeDisplayToId: planTypeData.planTypeDisplayToId ?? 2,
            imageAssetId: planTypeData.imageAssetId ?? null,
            imageUrl: planTypeData.imageUrl ?? null,
        };
        const response = await clientApiHttp.put(
            `/v2/client/${tenantId}/plan-type/single`,
            payload,
        );
        return response.data;
    } catch (error) {
        console.error('Error updating plan type:', error);
        throw error;
    }
};

/**
 * Fetches plan type detail categories by client plan type ID.
 *
 * @param {number} clientPlanTypeId - The ID of the client plan type
 * @param {number} planYearId - The plan year ID
 * @returns {Promise<Array>} A promise that resolves to the plan type detail categories
 */
export const fetchPlanTypeDetailCategories = async (
    clientPlanTypeId,
    planYearId = null,
) => {
    try {
        const tenantId = getTenantId();
        const params = new URLSearchParams();
        if (planYearId) params.append('planYearId', planYearId);

        const response = await clientApiHttp.get(
            `/v2/client/${tenantId}/plan-type-detail-category/${clientPlanTypeId}?${params.toString()}`,
        );
        return response.data;
    } catch (error) {
        console.error(
            `Error fetching plan type detail categories for plan type ID ${clientPlanTypeId}:`,
            error,
        );
        throw error;
    }
};

/**
 * Updates plan type detail categories by client plan type ID.
 *
 * @param {number} clientPlanTypeId - The ID of the client plan type
 * @param {Array} planTypeDetailCategoriesData - The updated categories data
 * @returns {Promise<Object>} A promise that resolves to the updated plan type detail categories data
 */
export const updatePlanTypeDetailCategories = async (
    clientPlanTypeId,
    planTypeDetailCategoriesData,
) => {
    try {
        const tenantId = getTenantId();
        const response = await clientApiHttp.put(
            `/v2/client/${tenantId}/plan-type-detail-category/${clientPlanTypeId}`,
            { planTypeDetailCategories: planTypeDetailCategoriesData },
        );
        return response.data;
    } catch (error) {
        console.error(
            `Error updating plan type detail categories for plan type ID ${clientPlanTypeId}:`,
            error,
        );
        throw error;
    }
};

/**
 * Fetches plan type detail (category items) by plan type detail category ID.
 *
 * @param {number} planTypeDetailCategoryId - The ID of the plan type detail category
 * @returns {Promise<Object>} A promise that resolves to the plan type detail data
 */
export const fetchPlanTypeDetail = async planTypeDetailCategoryId => {
    try {
        const tenantId = getTenantId();
        const response = await clientApiHttp.get(
            `/v2/client/${tenantId}/plan-type-detail/${planTypeDetailCategoryId}`,
        );
        return response.data;
    } catch (error) {
        console.error(
            `Error fetching plan type detail for category ID ${planTypeDetailCategoryId}:`,
            error,
        );
        throw error;
    }
};

/**
 * Updates plan type detail (category items) by plan type detail category ID.
 *
 * @param {number} planTypeDetailCategoryId - The ID of the plan type detail category
 * @param {Array} planTypeDetailData - The updated data for the plan type detail items
 * @returns {Promise<Object>} A promise that resolves to the updated plan type detail data
 */
export const updatePlanTypeDetail = async (
    planTypeDetailCategoryId,
    planTypeDetailData,
) => {
    try {
        const tenantId = getTenantId();
        const response = await clientApiHttp.put(
            `/v2/client/${tenantId}/plan-type-detail/${planTypeDetailCategoryId}`,
            { planTypeDetails: planTypeDetailData },
        );
        return response.data;
    } catch (error) {
        console.error(
            `Error updating plan type detail for category ID ${planTypeDetailCategoryId}:`,
            error,
        );
        throw error;
    }
};

export const planDetailReport = async (planTypeIds, planYearIds, planIds) => {
    try {
        const params = [];

        if (planTypeIds && planTypeIds.length > 0)
            params.push(new URLSearchParams({ planTypeIds }));

        if (planYearIds && planYearIds.length > 0)
            params.push(new URLSearchParams({ planYearIds }));

        if (planIds && planIds.length > 0)
            params.push(new URLSearchParams({ planIds }));

        let response;

        if (params.length > 0)
            response = await clientApiHttp.get(
                `/v2/client/${getTenantId()}/reports/plan-detail?${params.toString()}`,
            );
        else
            response = await clientApiHttp.get(
                `/v2/client/${getTenantId()}/reports/plan-detail`,
            );

        return response.data;
    } catch (error) {
        console.error('Error getting the plan detail report:', error);
        throw error;
    }
};

/**
 * Fetches a single ClientPlanType by id.
 *
 * @param {number} clientPlanTypeId - The ClientPlanType id
 * @param {number} planYearId - The plan year ID
 * @returns {Promise<Object>} The full ClientPlanType record
 */
export const fetchPlanType = async (clientPlanTypeId, planYearId = null) => {
    try {
        const tenantId = getTenantId();
        const params = new URLSearchParams();
        if (planYearId) params.append('planYearId', planYearId);

        const response = await clientApiHttp.get(
            `/v2/client/${tenantId}/plan-type/${clientPlanTypeId}?${params.toString()}`,
        );
        return response.data;
    } catch (error) {
        console.error(`Error fetching plan type ${clientPlanTypeId}:`, error);
        throw error;
    }
};

/**
 * Creates a new ClientPlanType.
 *
 * @param {Object} planTypeData - The plan type data to create
 * @returns {Promise<Object>} The newly created ClientPlanType
 */
export const createPlanType = async planTypeData => {
    try {
        const tenantId = getTenantId();
        const payload = {
            id: 0,
            planTypeId: planTypeData.planTypeId ?? null,
            duplicatedFromPlanTypeId:
                planTypeData.duplicatedFromPlanTypeId ?? null,
            name: planTypeData.name ?? planTypeData.nameOverride ?? null,
            code: planTypeData.code ?? null,
            planYearId: planTypeData.planYearId,
            populationGroupIds: planTypeData.populationGroupIds ?? [],
            planTypeDisplayToId: planTypeData.planTypeDisplayToId ?? 2,
            imageAssetId: planTypeData.imageAssetId ?? null,
            imageUrl: planTypeData.imageUrl ?? null,
        };
        const response = await clientApiHttp.post(
            `/v2/client/${tenantId}/plan-type`,
            payload,
        );
        return response.data;
    } catch (error) {
        console.error('Error creating plan type:', error);
        throw error;
    }
};

/**
 * Duplicates a ClientPlanType by posting a new record with duplicatedFromPlanTypeId
 * pointing to the source plan type's planTypeId.
 *
 * @param {number} clientPlanTypeId - Source ClientPlanType id
 * @param {Array} existingRows - The current list rows for computing unique names
 * @param {number} planYearId - The plan year ID for the new plan type
 * @returns {Promise<Object>} The newly created ClientPlanType
 */
export const duplicatePlanType = async (
    clientPlanTypeId,
    existingRows = [],
    planYearId = null,
    newCode = null,
) => {
    try {
        const source = existingRows.find(r => r.id === clientPlanTypeId);
        if (!source) {
            throw new Error(`Plan type ${clientPlanTypeId} not found in list`);
        }

        const existingNames = existingRows
            .map(r => r?.name || r?.planType?.name)
            .filter(Boolean);
        const sourceName = source.name || source.planType?.name || 'Plan Type';

        const newRecord = {
            planTypeId: null,
            duplicatedFromPlanTypeId:
                source.planTypeId ??
                source.planType?.id ??
                source.duplicatedFromPlanTypeId,
            name: getNameForDuplicate(sourceName, existingNames),
            code: newCode || null,
            planYearId: planYearId ?? source.planYearId,
            populationGroupIds: source.populationGroupIds ?? [],
            planTypeDisplayToId: source.planTypeDisplayToId ?? 2,
            imageAssetId: null,
            imageUrl: null,
        };

        return await createPlanType(newRecord);
    } catch (error) {
        console.error('Error duplicating plan type:', error);
        throw error;
    }
};

/**
 * Deletes a single non-system ClientPlanType.
 * Pairs with BACKEND_REQUIREMENTS_ADMIN.md §2c.
 */
export const deletePlanType = async clientPlanTypeId => {
    try {
        const tenantId = getTenantId();
        const response = await clientApiHttp.delete(
            `/v2/client/${tenantId}/plan-type/${clientPlanTypeId}`,
        );
        return response.data;
    } catch (error) {
        console.error('Error deleting plan type:', error);
        throw error;
    }
};

/**
 * Bulk deletes ClientPlanTypes by id.
 * Server-side: rejects if any target is system-generated.
 * Pairs with BACKEND_REQUIREMENTS_ADMIN.md §2c (bulk).
 */
export const deletePlanTypes = async clientPlanTypeIds => {
    try {
        if (
            !Array.isArray(clientPlanTypeIds) ||
            clientPlanTypeIds.length === 0
        ) {
            throw new TypeError('clientPlanTypeIds must be a non-empty array');
        }
        const tenantId = getTenantId();
        const params = new URLSearchParams();
        clientPlanTypeIds.forEach(id => params.append('ids', id));
        const response = await clientApiHttp.delete(
            `/v2/client/${tenantId}/plan-type?${params.toString()}`,
        );
        return response.data;
    } catch (error) {
        console.error('Error deleting plan types:', error);
        throw error;
    }
};

/**
 * Persists a new order for plan types.
 *
 * @param {Array} planTypeOrder - Array describing the desired plan type order
 * @returns {Promise<Object>} The API response
 */
export const updatePlanTypeOrder = async planTypeOrder => {
    try {
        if (
            planTypeOrder &&
            !Array.isArray(planTypeOrder?.clientPlanTypeOrders)
        ) {
            throw new TypeError('planTypeOrder must be an array');
        }

        const tenantId = getTenantId();
        const response = await clientApiHttp.put(
            `/v2/client/${tenantId}/plan-type/order`,
            planTypeOrder,
        );
        return response.data;
    } catch (error) {
        console.error('Error updating plan type order:', error);
        throw error;
    }
};
